import React, { Component } from 'react';
import PropTypes from 'prop-types';
import TagCloud from '../TagCloud/TagCloud';
import PlaylistFilesTable from './PlaylistFilesTable';
import TagControlledPlaylistPanel from './TagControlledPlaylistPanel';
import { getPermissions } from '../../utils/getPermissions';
import ToolTipIcon from '../ToolTipIcon/ToolTipIcon';

export default class PlaylistForm extends Component {
  state = {
    showErrors: false,
    errors: {},
    name: this.props.playlist?.name || '',
    description: this.props.playlist?.description || '',
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { showErrors } = this.state;

    if (showErrors) {
      this.validate(nextProps);
    }
  }

  componentDidUpdate(prevProps) {
    const { playlist } = this.props;

    if (playlist && playlist !== prevProps.playlist) {
      this.setState({
        name: playlist.name || '',
        description: playlist.description || '',
      });
    }
  }

  onChange = event => {
    const { onChange } = this.props;

    onChange(event.target.name, event.target.value);
  };

  onChangeTag = tags => {
    const { onChange } = this.props;

    onChange('tags', tags);
  };

  onChangeCheckbox = event => {
    const { onChange } = this.props;
    const { name, checked } = event.target;

    onChange(name, checked);
  };

  errorClass = error => (error ? 'error' : null);

  validate = nextProps => {
    const props = nextProps || this.props;
    const {
      playlist: { name },
    } = props;
    const errors = {};

    if (!name?.trim()) {
      errors.name = 'This field cannot be empty.';
    } else if (!this.isUniqueName(props)) {
      errors.name = 'This name is already in use.';
    }

    this.setState({ showErrors: true, errors });

    return !errors.name;
  };

  submit = event => {
    const { isNew, onAdd, onUpdate } = this.props;

    event.preventDefault();

    if (!this.validate()) {
      return;
    }

    if (isNew) {
      onAdd();
    } else {
      onUpdate();
    }
  };

  /**
   * Checks if the playlist name is unique among existing playlists.
   * @param {{playlists: Array, playlist: Object}} props - The component props.
   * @returns {boolean} - True if the name is unique, false otherwise.
   */
  isUniqueName = props => {
    const { playlists, playlist } = props;

    return (
      !Array.isArray(playlists) ||
      !playlists.some(item => {
        const name = playlist.name.toLowerCase().trim();
        const itemName = item.name.toLowerCase().trim();

        return +item.id !== +playlist.id && itemName === name;
      })
    );
  };

  getButtons = () => {
    const { auth, isNew, onCancel } = this.props;
    const p = getPermissions(auth);
    const add = (
      <button
        key="1"
        type="submit"
        data-test="button-add"
        className="flex-1 bg-btn-primary-form text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-black/20"
      >
        Add Playlist
      </button>
    );
    const update = (
      <button
        key="2"
        type="submit"
        data-test="button-update"
        className="flex-1 bg-btn-primary-form text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-black/20"
      >
        Save Changes
      </button>
    );
    const cancel = (
      <button
        key="3"
        type="button"
        data-test="button-cancel"
        className="px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs text-gray-400 hover:text-black transition-colors border border-transparent hover:border-gray-200"
        onClick={onCancel}
      >
        Cancel
      </button>
    );

    if (!p.writer) {
      return [cancel];
    }

    return isNew ? [add, cancel] : [update, cancel];
  };

  render() {
    const {
      auth,
      playlist,
      signs,
      sequences,
      schedules,
      playlists,
      campaigns,
      assignments,
      onSort,
      onAddFile,
      onRemoveFile,
    } = this.props;
    const { name, description, errors } = this.state;
    const p = getPermissions(auth);

    if (!playlist) {
      return null;
    }

    const { tags, tagControlled } = playlist;

    return (
      <div
        id="edit"
        className="form-signs lg:col-span-8 mt-4 bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-200 p-4"
      >
        <form className="space-y-6" onSubmit={this.submit}>
          <div className="grid grid-cols-2 gap-4">
            <div className="relative group">
              {this.errorClass(errors.name) && (
                <span className="text-sunrise font-bold  mt-1 mb-1">{errors.name}</span>
              )}
              <label
                htmlFor="playlist-input-name"
                className=" form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
              >
                Playlist Name
              </label>
              <input
                id="playlist-input-name"
                data-test="input-name"
                type="text"
                name="name"
                value={name}
                onChange={e => this.setState({ name: e.target.value })}
                onBlur={e => this.onChange(e)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    this.onChange(e);
                  }
                }}
                disabled={!p.writer}
                className="w-full text-xl font-bold px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none"
              />
            </div>

            <div className="relative group">
              <label
                htmlFor="playlist-description"
                className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
              >
                Description
              </label>
              <textarea
                id="playlist-description"
                data-test="input-desc"
                rows="1"
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                value={description}
                onChange={e => this.setState({ description: e.target.value })}
                onBlur={e => this.onChange(e)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    this.onChange(e);
                  }
                }}
                disabled={!p.writer}
                name="description"
                className="w-full text-xl font-medium px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none resize-none"
              />
            </div>

            <TagCloud tags={tags} onChange={this.onChangeTag} disabled={!p.writer} />
          </div>

          <div className="flex items-center">
            <input
              id="tags-controlled-checkbox"
              disabled={!p.writer}
              data-test="checkbox-tag-controlled"
              type="checkbox"
              name="tagControlled"
              checked={Boolean(tagControlled)}
              className="w-4 h-4 cursor-pointer mr-8"
              onChange={this.onChangeCheckbox}
            />
            <label
              htmlFor="tags-controlled-checkbox"
              className="cursor-pointer text-dark-night poppins-bold"
            >
              Tag Controlled Playlist
            </label>
          </div>

          <TagControlledPlaylistPanel
            playlist={playlist}
            signs={signs}
            sequences={sequences}
            schedules={schedules}
            playlists={playlists}
            campaigns={campaigns}
            assignments={assignments}
          />
          {p.writer && (
            <ToolTipIcon
              toolText="Add Files"
              data-test="add-button"
              type="button"
              className="pull-right add-files w-[50px] relative
            cursor-pointer h-[50px] mt-2 flex justify-center items-center bg-white rounded-lg"
              onClick={onAddFile}
            >
              <img
                src="/assets/images/icons/upload_icon.svg"
                alt="Add video to playlist"
                className="w-[50px] h-[50px] cursor-pointer"
              />
            </ToolTipIcon>
          )}
          <PlaylistFilesTable
            auth={auth}
            data={playlist.files}
            onSort={onSort}
            onRemoveFile={onRemoveFile}
          />
          <div className="pt-6 flex flex-col sm:flex-row gap-4">{this.getButtons()}</div>
        </form>
      </div>
    );
  }
}

PlaylistForm.propTypes = {
  auth: PropTypes.object.isRequired,
  playlist: PropTypes.object,
  signs: PropTypes.array,
  sequences: PropTypes.array,
  schedules: PropTypes.array,
  playlists: PropTypes.array,
  campaigns: PropTypes.array,
  assignments: PropTypes.array,
  isNew: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onAddFile: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
};
